/* ==========================================================================
   Blue Sky Cashmere B2B — Theme JavaScript
   ========================================================================== */

(function() {
  'use strict';

  /* --------------------------------------------------------------------------
     bfcache state reset (2026-05-16 bug-sweep)
     -------------------------------------------------------------------------- *
     Chrome's back/forward cache restores pages with their full state intact —
     including drawers that were open when the user navigated away. On rapid
     back/forward navigation this surfaces as a millisecond flash of the
     restored open drawer / overlay before the user's eyes catch up. Listen
     for the pageshow `persisted` flag and force-close every modal surface
     when the page is restored from cache.
     -------------------------------------------------------------------------- */
  window.addEventListener('pageshow', function(e) {
    if (!e.persisted) return;
    document.querySelectorAll(
      '[data-menu-drawer], [data-sample-cart-drawer], [data-search-drawer], .floating-contact.is-open, [data-lang-switcher].is-open, [data-nav-item].is-active, [data-accordion-item].is-open, [data-mobile-accordion-item].is-open, .site-footer__column.is-open'
    ).forEach(function(el) { el.classList.remove('is-open'); el.classList.remove('is-active'); });
    document.querySelectorAll(
      '[data-menu-overlay], [data-sample-cart-overlay]'
    ).forEach(function(el) { el.classList.remove('is-active'); });
    document.body.style.overflow = '';
    // §1.2 — bfcache restores the DOM verbatim, including `inert` left on the
    // background siblings by an open sample-cart drawer. Without this the restored
    // page is completely dead (every link/button unresponsive). Also reset stale
    // aria-expanded on any trigger restored in the "open" state.
    document.querySelectorAll('[inert]').forEach(function(el) { el.removeAttribute('inert'); });
    document.querySelectorAll('[aria-expanded="true"]').forEach(function(el) { el.setAttribute('aria-expanded', 'false'); });
  });

  /* --------------------------------------------------------------------------
     Header — Sticky scroll behavior
     -------------------------------------------------------------------------- */
  const header = document.querySelector('[data-header]');
  if (header) {
    let lastScroll = 0;
    const scrollThreshold = 10;

    window.addEventListener('scroll', function() {
      const currentScroll = window.pageYOffset;

      if (currentScroll > scrollThreshold) {
        header.classList.add('is-scrolled');
      } else {
        header.classList.remove('is-scrolled');
      }

      lastScroll = currentScroll;
    }, { passive: true });
  }

  /* --------------------------------------------------------------------------
     Mobile Menu Drawer
     -------------------------------------------------------------------------- */
  const menuTrigger = document.querySelector('[data-menu-trigger]');
  const menuDrawer = document.querySelector('[data-menu-drawer]');
  const menuClose = document.querySelector('[data-menu-close]');
  const menuOverlay = document.querySelector('[data-menu-overlay]');

  let menuLastFocused = null;
  const MENU_FOCUSABLE = 'button:not([hidden]):not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function openMenu() {
    if (!menuDrawer) return;
    menuLastFocused = document.activeElement;
    menuDrawer.classList.add('is-open');
    if (menuOverlay) menuOverlay.classList.add('is-active');
    document.body.style.overflow = 'hidden';
    menuTrigger?.setAttribute('aria-expanded', 'true');
    // §2.3 — inert the rest of the page (top bar + main + footer) so AT and Tab
    // can't leave the open drawer. The drawer lives inside <header>, so we inert
    // the header's inner bar (sibling) rather than <header> itself.
    document.querySelectorAll('main, footer, .site-header__inner').forEach(function(el) {
      if (menuDrawer.contains(el) || el.contains(menuDrawer)) return;
      el.setAttribute('inert', '');
    });
    // Move focus into the drawer (first visible interactive element).
    const focusables = menuDrawer.querySelectorAll(MENU_FOCUSABLE);
    const first = Array.prototype.find.call(focusables, function(el) { return el.offsetParent !== null; });
    if (first) requestAnimationFrame(function() { first.focus(); });
  }

  function closeMenu() {
    if (!menuDrawer) return;
    menuDrawer.classList.remove('is-open');
    if (menuOverlay) menuOverlay.classList.remove('is-active');
    document.body.style.overflow = '';
    menuTrigger?.setAttribute('aria-expanded', 'false');
    document.querySelectorAll('[inert]').forEach(function(el) { el.removeAttribute('inert'); });
    // Restore focus to the trigger that opened the drawer.
    if (menuLastFocused && typeof menuLastFocused.focus === 'function') {
      try { menuLastFocused.focus(); } catch (_) { /* element may be gone */ }
    }
    menuLastFocused = null;
  }

  menuTrigger?.addEventListener('click', openMenu);
  menuClose?.addEventListener('click', closeMenu);
  menuOverlay?.addEventListener('click', closeMenu);

  // §2.3 — Escape closes the drawer; Tab is trapped within it while open.
  document.addEventListener('keydown', function(e) {
    if (!menuDrawer || !menuDrawer.classList.contains('is-open')) return;
    if (e.key === 'Escape') { closeMenu(); return; }
    if (e.key === 'Tab') {
      const f = Array.prototype.filter.call(
        menuDrawer.querySelectorAll(MENU_FOCUSABLE),
        function(el) { return el.offsetParent !== null; }
      );
      if (!f.length) return;
      const first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  });

  /* --------------------------------------------------------------------------
     Mobile Menu Accordions
     -------------------------------------------------------------------------- */
  /* CSS-driven accordion (Phase 5): only toggle .is-open + ARIA — height
     is animated via grid-template-rows: 0fr → 1fr in CSS. */
  document.querySelectorAll('[data-mobile-accordion-trigger]').forEach(function(trigger) {
    trigger.addEventListener('click', function() {
      const item = this.closest('[data-mobile-accordion-item]');
      if (!item) return;
      const isOpen = item.classList.contains('is-open');
      // Close all other items (single-open accordion)
      document.querySelectorAll('[data-mobile-accordion-item].is-open').forEach(function(openItem) {
        if (openItem !== item) {
          openItem.classList.remove('is-open');
          const otherTrigger = openItem.querySelector('[data-mobile-accordion-trigger]');
          if (otherTrigger) otherTrigger.setAttribute('aria-expanded', 'false');
        }
      });
      item.classList.toggle('is-open', !isOpen);
      this.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
    });
  });

  /* --------------------------------------------------------------------------
     Desktop Mega Menu / Dropdowns
     -------------------------------------------------------------------------- */
  document.querySelectorAll('[data-mega-trigger]').forEach(function(trigger) {
    const parent = trigger.closest('[data-nav-item]');
    const dropdown = parent?.querySelector('[data-mega-content]');
    if (!parent || !dropdown) return;
    let showTimer, hideTimer, openedByHover = false;
    // Keep a contained panel inside the viewport (from live, 23 Sep 2026). The CSS anchors
    // each panel to its trigger, but where a trigger lands depends on the language:
    // Mongolian and Russian nav labels are ~1.2x English, which pushed a right-anchored
    // panel 5-22px off the left edge. Measuring on open covers every locale, width and
    // engine. The catalogue panel is full-bleed.
    function fitPanel() {
      if (dropdown.classList.contains('site-header__panel--catalogue')) return;
      dropdown.style.transform = '';
      const r = dropdown.getBoundingClientRect();
      const gutter = 16;
      const vw = document.documentElement.clientWidth;
      let dx = 0;
      if (r.left < gutter) dx = gutter - r.left;
      else if (r.right > vw - gutter) dx = (vw - gutter) - r.right;
      if (dx) dropdown.style.transform = 'translateX(' + Math.round(dx) + 'px)';
    }
    function setOpen(open, hover = false) {
      clearTimeout(showTimer); clearTimeout(hideTimer);
      if (open) fitPanel();
      if (open) document.querySelectorAll('[data-nav-item].is-active').forEach(function(item) {
        if (item !== parent) {
          item.classList.remove('is-active');
          item.querySelector('[data-mega-trigger]')?.setAttribute('aria-expanded', 'false');
        }
      });
      parent.classList.toggle('is-active', open);
      trigger.setAttribute('aria-expanded', String(open));
      openedByHover = open && hover;
    }
    parent.addEventListener('mouseenter', function() {
      clearTimeout(hideTimer);
      if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches || parent.classList.contains('is-active')) return;
      showTimer = setTimeout(function() { setOpen(true, true); }, 180);
    });
    parent.addEventListener('mouseleave', function() {
      clearTimeout(showTimer);
      hideTimer = setTimeout(function() { if (!parent.contains(document.activeElement)) setOpen(false); }, 220);
    });
    trigger.addEventListener('click', function() {
      setOpen(openedByHover || !parent.classList.contains('is-active'));
    });
    parent.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' && parent.classList.contains('is-active')) {
        setOpen(false); trigger.focus();
      }
    });
    parent.addEventListener('focusout', function(e) { if (!parent.contains(e.relatedTarget)) setOpen(false); });
    document.addEventListener('click', function(e) { if (!parent.contains(e.target)) setOpen(false); });
  });

  /* --------------------------------------------------------------------------
     Accordion (general purpose)
     -------------------------------------------------------------------------- */
  /* CSS-driven accordion (Phase 5): toggle class only, height handled by CSS. */
  document.querySelectorAll('[data-accordion-trigger]').forEach(function(trigger) {
    trigger.addEventListener('click', function() {
      const item = this.closest('[data-accordion-item]');
      if (!item) return;
      const isOpen = item.classList.toggle('is-open');
      this.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    });
  });

  /* --------------------------------------------------------------------------
     Announcement Bar Rotation
     -------------------------------------------------------------------------- */
  const announcementSlides = document.querySelectorAll('[data-announcement-slide]');
  if (announcementSlides.length > 1) {
    let currentSlide = 0;
    let arTimer = null;
    const arToggle = document.querySelector('[data-announcement-toggle]');
    const arReduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    function arAdvance() {
      announcementSlides[currentSlide].classList.remove('is-active');
      currentSlide = (currentSlide + 1) % announcementSlides.length;
      announcementSlides[currentSlide].classList.add('is-active');
    }
    function arStart() { if (!arTimer) arTimer = setInterval(arAdvance, 5000); }
    function arStop() { if (arTimer) { clearInterval(arTimer); arTimer = null; } }
    // §2.1(b) — a single mechanism drives both the toggle button and the
    // reduced-motion gate. paused=true stops the interval; the button label +
    // icon flip to reflect the action the control will next perform.
    function arSetPaused(paused) {
      if (paused) arStop(); else arStart();
      if (arToggle) {
        arToggle.classList.toggle('is-paused', paused);
        arToggle.setAttribute('aria-label', paused ? arToggle.dataset.labelPlay : arToggle.dataset.labelPause);
      }
    }
    if (arToggle) {
      arToggle.addEventListener('click', function() { arSetPaused(!!arTimer); });
    }
    // Respect the OS reduced-motion preference: start paused, and react to
    // live changes (a JS setInterval can't be stopped by a CSS media query).
    arSetPaused(arReduce.matches);
    if (arReduce.addEventListener) {
      arReduce.addEventListener('change', function(e) { arSetPaused(e.matches); });
    }
  }

  /* --------------------------------------------------------------------------
     §2.1(a) Marquee pause toggle (cert marquee) — keyboard/touch-accessible
     pause. CSS handles hover-pause and the reduced-motion "no animation" case.
     -------------------------------------------------------------------------- */
  document.querySelectorAll('[data-marquee-toggle]').forEach(function(btn) {
    const section = btn.closest('[data-section-id]') || document;
    const marquee = section.querySelector('[data-marquee]');
    if (!marquee) return;
    btn.addEventListener('click', function() {
      const paused = marquee.classList.toggle('is-paused');
      btn.classList.toggle('is-paused', paused);
      btn.setAttribute('aria-label', paused ? btn.dataset.labelPlay : btn.dataset.labelPause);
    });
  });

  /* --------------------------------------------------------------------------
     §2.1(c) Hero video pause toggle + reduced-motion auto-pause. Dormant unless
     a hero video is configured (the element is absent otherwise).
     -------------------------------------------------------------------------- */
  (function() {
    const heroVideo = document.querySelector('.hero__bg video');
    if (!heroVideo) return;
    const hvToggle = document.querySelector('[data-hero-video-toggle]');
    const hvReduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    function hvSetPaused(paused) {
      if (paused) { heroVideo.pause(); } else { heroVideo.play().catch(function() {}); }
      if (hvToggle) {
        hvToggle.classList.toggle('is-paused', paused);
        hvToggle.setAttribute('aria-label', paused ? hvToggle.dataset.labelPlay : hvToggle.dataset.labelPause);
      }
    }
    if (hvReduce.matches) hvSetPaused(true);
    if (hvToggle) {
      hvToggle.addEventListener('click', function() { hvSetPaused(!heroVideo.paused); });
    }
    if (hvReduce.addEventListener) {
      hvReduce.addEventListener('change', function(e) { hvSetPaused(e.matches); });
    }
  })();

  /* --------------------------------------------------------------------------
     Smooth Scroll for Anchor Links
     -------------------------------------------------------------------------- */
  document.querySelectorAll('a[href^="#"]').forEach(function(anchor) {
    anchor.addEventListener('click', function(e) {
      const targetId = this.getAttribute('href');
      if (targetId === '#') return;

      // §1.9(c) — getElementById, not querySelector: ids like "3-ply" are invalid CSS
      // selectors, so querySelector('#3-ply') throws a SyntaxError and aborts the handler.
      const target = document.getElementById(targetId.slice(1));
      if (!target) return;

      e.preventDefault();
      const headerOffset = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 65;
      const announcementOffset = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--announcement-height')) || 36;
      const offset = headerOffset + announcementOffset + 20;

      window.scrollTo({
        top: target.offsetTop - offset,
        // §2.6 — honour reduced-motion; a JS smooth scroll ignores the CSS pref.
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
      });
      // Move keyboard focus to the target as well, not just the scroll position. Without
      // this, preventDefault() above also cancelled the browser's own focus move, so
      // "Skip to content" left focus on the skip link and the next Tab went back into the
      // header (a11y audit 7 Oct 2026, BSC-04). Same for every in-page link.
      if (!target.matches('a[href], button, input, select, textarea, [tabindex]')) {
        target.setAttribute('tabindex', '-1');
        target.classList.add('is-focus-target');   // no outline round a whole section (base.css)
      }
      try { target.focus({ preventScroll: true }); } catch (_) { target.focus(); }
    });
  });

  /* --------------------------------------------------------------------------
     Form Validation
     -------------------------------------------------------------------------- */
  /* PHASE-PRE-LAUNCH RE-WORK: original implementation attached `submit` listener
     to the per-form element in BUBBLE phase. The Shopify hCaptcha integration
     and the auto-injected "contact form" wrappers intercept submit events
     before they bubble back up — so the per-form bubble handler never fired
     (verified via Cowork DOM inspection: bscValidateBound was set on the form
     but no validation banner, error class, or aria-invalid attributes ever
     appeared). Switching to:
       (a) Document-level submit listener in CAPTURE phase — runs before any
           wrapper/captcha integration can stopPropagation.
       (b) Eager `novalidate` set on every form with [data-validate-form]
           markers so the native HTML5 popup never fires.
       (c) Re-applied on MutationObserver in case any wrapper script clones the
           form into a different DOM node. */

  function _bscFormFromTarget(target) {
    if (!target) return null;
    if (target.tagName === 'FORM') return target;
    if (target.matches && target.matches('[data-validate-form]')) {
      return target.tagName === 'FORM' ? target : target.closest('form');
    }
    const marker = target.querySelector && target.querySelector('[data-validate-form]');
    return marker ? (marker.tagName === 'FORM' ? marker : marker.closest('form')) : null;
  }

  function _bscIsValidatedForm(form) {
    if (!form) return false;
    if (form.dataset.validateForm !== undefined) return true; // form has the attr
    return !!form.querySelector('[data-validate-form]');       // button inside has the attr
  }

  function _bscApplyNovalidate() {
    document.querySelectorAll('[data-validate-form]').forEach(function(marker) {
      const form = marker.tagName === 'FORM' ? marker : marker.closest('form');
      if (form && !form.hasAttribute('novalidate')) {
        form.setAttribute('novalidate', 'novalidate');
        form.dataset.bscValidateBound = '1';
      }
    });
  }

  // Run immediately + on DOMContentLoaded + on subsequent DOM mutations to
  // survive any later wrapper scripts that clone or re-render the form.
  _bscApplyNovalidate();
  document.addEventListener('DOMContentLoaded', _bscApplyNovalidate);
  if ('MutationObserver' in window) {
    new MutationObserver(_bscApplyNovalidate).observe(document.documentElement, {
      childList: true, subtree: true
    });
  }

  /* PHASE-PRE-LAUNCH ROUND-3: bind on document `click` in capture phase (not
     `submit`) — Shopify auto-injects hCaptcha for {% form 'contact' %} forms,
     and on the FIRST click, hCaptcha eats the submit event entirely to run a
     challenge. Listening for `click` catches the user's intent EARLIER, before
     hCaptcha can intercept. We then validate, and if any required field is
     empty, preventDefault on the click — which stops the button from triggering
     hCaptcha at all. */
  // Error wording in the page's language, from layout/theme.liquid (BSC-10). English fallback
  // only if that block is ever missing.
  const BSC_MSG = Object.assign({
    required: '__FIELD__ is required', email: 'Please enter a valid email address',
    one: 'Please complete this required field:', many: 'Please complete these required fields (__N__):',
    dismiss: 'Dismiss error', field: 'This field'
  }, window.BSC_FORM_MSG || {});

  function _bscRunValidation(e, btn) {
    const form = _bscFormFromTarget(btn);
    if (!_bscIsValidatedForm(form)) return true; // not one of our forms — let it through

    let isValid = true;
    const invalidFields = [];

      /* PHASE-PRE-LAUNCH: aggregate multi-checkbox groups into a single hidden
         input. data-multi-source="X" wraps the checkboxes; the corresponding
         data-multi-target="X" hidden input receives the comma-joined values. */
      form.querySelectorAll('[data-multi-source]').forEach(function(group) {
        const key = group.getAttribute('data-multi-source');
        const target = form.querySelector('input[data-multi-target="' + key + '"]');
        if (!target) return;
        const values = Array.from(group.querySelectorAll('[data-multi-checkbox]:checked'))
          .map(function(cb) { return cb.value; });
        target.value = values.join(', ');
      });

      // Re-enable any fields we'd previously disabled on a failed-validation attempt
      // so the user can fill them in this round.
      form.querySelectorAll('[data-was-empty]').forEach(function(f) {
        f.disabled = false;
        f.removeAttribute('data-was-empty');
      });

      // Clear previous errors
      form.querySelectorAll('.form-error').forEach(function(err) { err.remove(); });
      form.querySelectorAll('.is-error').forEach(function(el) { el.classList.remove('is-error'); });
      form.querySelectorAll('[data-form-error-banner]').forEach(function(b) { b.remove(); });

      function fieldLabel(field) {
        if (field.id) {
          const lbl = form.querySelector('label[for="' + field.id + '"]');
          if (lbl) return lbl.textContent.replace(/[*\s]+$/, '').trim();
        }
        return field.getAttribute('aria-label') || field.name || BSC_MSG.field;
      }

      function flagField(field, message) {
        isValid = false;
        invalidFields.push({ field: field, message: message });
        field.classList.add('is-error');
        field.setAttribute('aria-invalid', 'true');
        const error = document.createElement('div');
        error.className = 'form-error';
        error.textContent = message;
        // Tie the message to its field so a screen reader reads it with the field (BSC-10).
        error.id = (field.id || field.name || 'field').replace(/[^\w-]/g, '-') + '-error';
        const describedBy = (field.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean);
        if (describedBy.indexOf(error.id) < 0) { describedBy.push(error.id); field.setAttribute('aria-describedby', describedBy.join(' ')); }
        // Insert after the field wrapper for predictable visual placement
        const insertionParent = field.closest('.form-group') || field.parentNode;
        insertionParent.appendChild(error);

        /* PHASE-PRE-LAUNCH ROUND-4: auto-clear the per-field error as soon as
           the user starts typing/fixing the field. Bind once per error; the
           handler removes itself after firing. */
        const clearOnFix = function() {
          field.classList.remove('is-error');
          field.removeAttribute('aria-invalid');
          const keep = (field.getAttribute('aria-describedby') || '').split(/\s+/).filter(function(x) { return x && x !== error.id; });
          if (keep.length) field.setAttribute('aria-describedby', keep.join(' ')); else field.removeAttribute('aria-describedby');
          if (error.parentNode) error.parentNode.removeChild(error);
          field.removeEventListener('input', clearOnFix);
          field.removeEventListener('change', clearOnFix);
        };
        field.addEventListener('input', clearOnFix);
        field.addEventListener('change', clearOnFix); // covers select / checkbox
      }

      // Validate required fields
      form.querySelectorAll('[required]').forEach(function(field) {
        const val = (field.value || '').trim();
        if (field.type === 'checkbox' && !field.checked) {
          flagField(field, BSC_MSG.required.replace('__FIELD__', fieldLabel(field)));
        } else if (!val) {
          flagField(field, BSC_MSG.required.replace('__FIELD__', fieldLabel(field)));
        }
      });

      // Validate email fields
      form.querySelectorAll('input[type="email"]').forEach(function(field) {
        if (field.classList.contains('is-error')) return; // already flagged as empty
        if (field.value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(field.value)) {
          flagField(field, BSC_MSG.email);
        }
      });

      if (!isValid) {
        e.preventDefault();
        e.stopImmediatePropagation();

        /* Defensive cleanup: another submit handler (Shopify wrapper / hCaptcha)
           may have added the spinner / submitting class before we fired in
           capture phase. Strip it so the UI doesn't lie about success. */
        form.classList.remove('form-submitting');
        form.querySelectorAll('.is-loading').forEach(function(el) {
          el.classList.remove('is-loading');
          el.disabled = false;
        });

        /* PHASE-PRE-LAUNCH ROUND-4: clear any previous toast before adding a
           new one. Without this, repeated submits stack toasts on top of each
           other (Cowork round-3 finding). Remove from anywhere in the DOM. */
        document.querySelectorAll('[data-form-error-banner]').forEach(function(prev) {
          if (prev.parentNode) prev.parentNode.removeChild(prev);
        });

        /* PHASE-PRE-LAUNCH ROUND-3: render the error banner as position:FIXED
           (toast-style) so it's visible regardless of the user's scroll
           position. Avoids the scrollIntoView race conditions we hit earlier. */
        const banner = document.createElement('div');
        banner.className = 'form-message--error form-message--error-toast';
        banner.setAttribute('data-form-error-banner', '');
        banner.setAttribute('role', 'alert');
        banner.setAttribute('tabindex', '-1');
        const title = document.createElement('p');
        title.className = 'form-message__title';
        title.textContent = invalidFields.length === 1
          ? BSC_MSG.one
          : BSC_MSG.many.replace('__N__', invalidFields.length);
        banner.appendChild(title);
        const list = document.createElement('ul');
        invalidFields.forEach(function(f) {
          const li = document.createElement('li');
          li.textContent = f.message;
          list.appendChild(li);
        });
        banner.appendChild(list);
        // Append to BODY so position:fixed isn't constrained by a transformed ancestor
        document.body.appendChild(banner);

        // Auto-focus banner for screen readers + give it a slight reveal animation
        requestAnimationFrame(function() {
          banner.classList.add('is-visible');
          banner.focus({ preventScroll: true });
        });

        // Banner persists until the user re-submits (next round will clear it).
        // Add a close button so users can dismiss it manually.
        const closeBtn = document.createElement('button');
        closeBtn.type = 'button';
        closeBtn.className = 'form-message__close';
        closeBtn.setAttribute('aria-label', BSC_MSG.dismiss);
        closeBtn.innerHTML = '&times;';
        closeBtn.addEventListener('click', function() {
          if (banner.parentNode) banner.parentNode.removeChild(banner);
        });
        banner.appendChild(closeBtn);

        return false;
      }

      /* PHASE-PRE-LAUNCH: disable empty OPTIONAL fields so they don't appear
         as blank lines in the admin notification email ("VAT Number:" with no
         value, etc). Disabled inputs are excluded from form submission. Done
         AFTER validation so the user is never blocked from filling required
         ones on a re-attempt. */
      form.querySelectorAll('input[name^="contact["]:not([required]):not([type="hidden"]):not([type="checkbox"]):not([type="radio"]), textarea[name^="contact["]:not([required]), select[name^="contact["]:not([required])').forEach(function(field) {
        if (!field.value || !field.value.trim()) {
          field.disabled = true;
          field.setAttribute('data-was-empty', '1');
        }
      });
      // Hidden aggregator fields: drop if no boxes were checked.
      form.querySelectorAll('input[data-multi-target]').forEach(function(field) {
        if (!field.value) {
          field.disabled = true;
          field.setAttribute('data-was-empty', '1');
        }
      });

      /* PHASE-POST-LAUNCH-FIX (2026-05-28): visual loading state ONLY — do NOT
         disable the button here. Setting `disabled = true` during the click
         event chain (in capture phase) prevents the browser from triggering
         the form's default submit action — the click fires, but the disabled
         state causes the browser to skip the implicit form-submit step that
         would normally follow. Result: form hangs forever in loading state
         with no POST fired (Cowork reproduced this for Quote + Samples on
         2026-05-28). Stick with `is-loading` class for visual feedback;
         the recovery timeout below will catch genuinely-stuck submissions.
         If a double-click protection is needed, do it on the SUBMIT event
         (bubble phase), AFTER the browser has dispatched the form-submit. */
      const submitBtn = form.querySelector('button[type="submit"], input[type="submit"]');
      if (submitBtn) {
        submitBtn.classList.add('is-loading');
        const originalText = submitBtn.textContent;
        submitBtn.dataset.originalText = originalText;
      }
      form.classList.add('form-submitting');

      /* Defer the actual disabled state until AFTER the browser dispatches
         the form-submit event. Listening once on the form's bubble-phase
         submit, we set disabled = true so a second click is impossible.
         This runs AFTER the browser's default-action has already been
         scheduled, so it can't cancel submission. */
      if (submitBtn && !submitBtn.dataset.bscDeferredDisableBound) {
        submitBtn.dataset.bscDeferredDisableBound = '1';
        form.addEventListener('submit', function _bscDeferredDisable() {
          // Use setTimeout to set disabled AFTER the submit handler chain has
          // released control back to the browser's submission machinery.
          setTimeout(function() { submitBtn.disabled = true; }, 0);
          form.removeEventListener('submit', _bscDeferredDisable);
        }, false);
      }

      /* PHASE-PRE-LAUNCH ROUND-5: submission-hang recovery — REVISED. Previous
         15s timeout was too aggressive: hCaptcha challenges legitimately take
         5-20 seconds to solve (the user clicking through "select all buses" UI),
         so the toast was firing DURING valid submissions and confusing users.
         New behavior:
           - 45 second base timeout (up from 15s)
           - Before firing, check if an hCaptcha challenge iframe is visible;
             if so, the user is mid-challenge — wait another 30s and re-check
           - Only fire if form is still stuck AND no captcha challenge is up */
      if (form.dataset.bscRecoveryTimer) {
        clearTimeout(parseInt(form.dataset.bscRecoveryTimer, 10));
      }
      function _bscMaybeFireRecovery() {
        if (!form.classList.contains('form-submitting')) return;
        // Skip if hCaptcha is showing a challenge — the user is interacting
        const captchaVisible = Array.from(document.querySelectorAll('iframe[src*="hcaptcha.com"]'))
          .some(function(iframe) {
            const r = iframe.getBoundingClientRect();
            return r.width > 50 && r.height > 50;
          });
        if (captchaVisible) {
          // User is solving captcha; check again in 30s
          const retry = setTimeout(_bscMaybeFireRecovery, 30000);
          form.dataset.bscRecoveryTimer = String(retry);
          return;
        }
        form.classList.remove('form-submitting');
        // §1.9(b) — clear the "already validated this round" flag so the next submit
        // re-validates. Left stale at '1' (a hung/aborted submit), the submit-phase
        // handler would skip validation and let one unvalidated submit through.
        form.dataset.bscRoundDone = '';
        if (submitBtn) {
          submitBtn.classList.remove('is-loading');
          submitBtn.disabled = false;
          // §1.9(a) — re-arm the deferred double-submit guard. It removes itself after the
          // first submit and is gated by this flag; without clearing it here the guard stays
          // permanently disarmed after a recovery, leaving the next submit unprotected.
          delete submitBtn.dataset.bscDeferredDisableBound;
        }
        // Show a recovery toast — same toast component used for validation.
        document.querySelectorAll('[data-form-error-banner]').forEach(function(prev) {
          if (prev.parentNode) prev.parentNode.removeChild(prev);
        });
        const banner = document.createElement('div');
        banner.className = 'form-message--error form-message--error-toast';
        banner.setAttribute('data-form-error-banner', '');
        banner.setAttribute('role', 'alert');
        banner.setAttribute('tabindex', '-1');
        const title = document.createElement('p');
        title.className = 'form-message__title';
        title.textContent = 'We didn\'t hear back from the server.';
        banner.appendChild(title);
        const msg = document.createElement('p');
        msg.style.margin = '8px 0 0';
        msg.style.fontSize = '13px';
        msg.textContent = 'This sometimes happens after a few failed attempts. Wait a minute and try once more, or email us directly at b2b@blueskycashmere.trade.';
        banner.appendChild(msg);
        const closeBtn = document.createElement('button');
        closeBtn.type = 'button';
        closeBtn.className = 'form-message__close';
        closeBtn.setAttribute('aria-label', 'Dismiss');
        closeBtn.innerHTML = '&times;';
        closeBtn.addEventListener('click', function() {
          if (banner.parentNode) banner.parentNode.removeChild(banner);
        });
        banner.appendChild(closeBtn);
        document.body.appendChild(banner);
        requestAnimationFrame(function() { banner.classList.add('is-visible'); banner.focus({ preventScroll: true }); });
      }
      const timerId = setTimeout(_bscMaybeFireRecovery, 45000);
      form.dataset.bscRecoveryTimer = String(timerId);
      return true;
  } /* end _bscRunValidation */

  /* Click-phase intercept: runs BEFORE hCaptcha can swallow the first click. */
  document.addEventListener('click', function(e) {
    const btn = e.target.closest('button[type="submit"], input[type="submit"]');
    if (!btn) return;
    const form = _bscFormFromTarget(btn);
    if (!_bscIsValidatedForm(form)) return;
    const passed = _bscRunValidation(e, btn);
    if (!passed) {
      e.preventDefault();
      e.stopImmediatePropagation();
    }
  }, true);

  /* Submit-phase intercept (capture): fallback for keyboard Enter / form.submit() */
  document.addEventListener('submit', function(e) {
    const form = _bscFormFromTarget(e.target);
    if (!_bscIsValidatedForm(form)) return;
    const submitter = e.submitter || form.querySelector('button[type="submit"], input[type="submit"]');
    if (form.dataset.bscRoundDone === '1') {
      form.dataset.bscRoundDone = '';
      return; // already validated this round (via click handler); let submit through
    }
    const passed = _bscRunValidation(e, submitter);
    if (!passed) {
      e.preventDefault();
      e.stopImmediatePropagation();
    } else {
      form.dataset.bscRoundDone = '1';
    }
  }, true); /* CAPTURE phase: runs before any hCaptcha / Shopify wrapper can intercept */

  /* --------------------------------------------------------------------------
     Search drawer
     -------------------------------------------------------------------------- */
  const searchTrigger = document.querySelector('[data-search-trigger]');
  const searchDrawer = document.querySelector('[data-search-drawer]');
  const searchClose = document.querySelector('[data-search-close]');
  const searchInput = document.querySelector('[data-search-input]');

  /* 14 Oct 2026 (buyer review): pictures nobody has asked to see yet wait until they are
     needed. Header menu photos and the search drawer's colour chips carry data-bsc-src and
     load the first time that menu / the drawer opens (they are decorative, alt=""). A product
     card's second (hover) photo carries data-gg-hover-src and loads the first time the
     pointer reaches THAT card, instead of every card on the page at once. */
  function bscWakeImages(root, attr) {
    if (!root) return;
    root.querySelectorAll('img[' + attr + ']').forEach(function(im) {
      im.src = im.getAttribute(attr);
      im.removeAttribute(attr);
    });
  }
  window.bscWakeImages = bscWakeImages;
  var bscCanHover = false;
  try { bscCanHover = window.matchMedia('(hover: hover)').matches; } catch (e) {}
  function bscWakeFrom(t) {
    if (!t || !t.closest) return;
    var nav = t.closest('[data-nav-item]');
    if (nav) bscWakeImages(nav, 'data-bsc-src');
    if (bscCanHover) {
      var card = t.closest('.gg-card, .ga-card');
      if (card) bscWakeImages(card, 'data-gg-hover-src');
    }
  }
  document.addEventListener('pointerover', function(e) { bscWakeFrom(e.target); }, { passive: true });
  document.addEventListener('focusin', function(e) { bscWakeFrom(e.target); });

  function openSearch() {
    if (!searchDrawer) return;
    bscWakeImages(searchDrawer, 'data-bsc-src');
    searchDrawer.classList.add('is-open');
    searchTrigger?.setAttribute('aria-expanded', 'true');
    setTimeout(function() { searchInput?.focus(); }, 100);
  }
  function closeSearch(restoreFocus) {
    if (!searchDrawer) return;
    searchDrawer.classList.remove('is-open');
    searchTrigger?.setAttribute('aria-expanded', 'false');
    // §2.6 — return focus to the trigger on keyboard-initiated close (Escape /
    // close button) so focus doesn't fall to <body> when the input hides.
    if (restoreFocus && searchTrigger) searchTrigger.focus();
  }
  searchTrigger?.addEventListener('click', function() {
    searchDrawer.classList.contains('is-open') ? closeSearch() : openSearch();
  });
  searchClose?.addEventListener('click', function() { closeSearch(true); });
  document.addEventListener('keydown', function(e) {
    if (e.key === 'Escape' && searchDrawer?.classList.contains('is-open')) closeSearch(true);
    if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
      // Leave the shortcut to the browser while someone is typing in a form
      // (Ctrl+K is the address-bar search in Chrome, Edge and Firefox).
      const t = e.target;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
      e.preventDefault();
      // Open-only. The duplicate ⌘K binding in the global keydown handler was removed (§1.4).
      if (!searchDrawer?.classList.contains('is-open')) openSearch();
    }
  });
  document.addEventListener('click', function(e) {
    if (!searchDrawer?.classList.contains('is-open')) return;
    if (searchDrawer.contains(e.target) || searchTrigger?.contains(e.target)) return;
    closeSearch();
  });

  /* --------------------------------------------------------------------------
     Language preference (30-day cookie)
     Shopify only persists locale via URL prefix or session cookie. Without
     subfolder URLs configured in Markets, locale is forgotten on navigation.
     We layer a 30-day cookie that:
       1. Captures the user's choice when they pick a language
       2. On any page load where Shopify's current locale doesn't match the
          cookie, silently submits a /localization form to switch back
     -------------------------------------------------------------------------- */
  (function() {
    const COOKIE_NAME = 'bsc_lang';
    const COOKIE_DAYS = 30;
    const REDIRECT_KEY = 'bsc_lang_redirect_path';

    function setLangCookie(code) {
      const expires = new Date(Date.now() + COOKIE_DAYS * 86400000).toUTCString();
      document.cookie = COOKIE_NAME + '=' + encodeURIComponent(code) +
        '; path=/; expires=' + expires + '; SameSite=Lax';
    }
    function getLangCookie() {
      const m = document.cookie.match(new RegExp('(?:^|;\\s*)' + COOKIE_NAME + '=([^;]+)'));
      return m ? decodeURIComponent(m[1]) : null;
    }

    // Capture clicks on any locale_code submit button (header switcher + mobile drawer)
    document.querySelectorAll('button[name="locale_code"]').forEach(function(btn) {
      btn.addEventListener('click', function() {
        if (btn.value) setLangCookie(btn.value);
      });
    });

    // Restore preferred language on load if Shopify rendered a different one
    const preferred = getLangCookie();
    if (!preferred) return;
    const current = (document.documentElement.lang || '').toLowerCase().split('-')[0];
    // Compare PRIMARY subtags on both sides. The cookie stores the full locale
    // code (e.g. "zh-CN") while <html lang> may differ in region; comparing only
    // the full strings made region-tagged locales (zh-CN) never match → a redirect
    // fired on every load even when already on the correct locale (audit flash bug).
    const preferredLang = preferred.toLowerCase().split('-')[0];
    if (!current || preferredLang === current) return;
    // Avoid redirect loops on the same path
    // §1.8 — storage can be blocked (private mode / locked-down corporate browser). An
    // uncaught throw here escapes the outer IIFE and kills EVERY later init (sample cart,
    // floating contact, tabs…). Guard it and just skip the loop-protection if unavailable.
    try { if (sessionStorage.getItem(REDIRECT_KEY) === window.location.pathname) return; } catch (e) {}

    // Find Shopify's own localization form (already on the page via {% form 'localization' %})
    // — it has the form_type, utf8, and any CSRF metadata Shopify expects. Building a form
    // from scratch lands at /localization with an error.
    const targetBtn = document.querySelector(
      'form[action*="/localization"] button[name="locale_code"][value="' + preferred + '"]'
    );
    if (!targetBtn) return; // language not available on this storefront — silently bail
    try { sessionStorage.setItem(REDIRECT_KEY, window.location.pathname); } catch (e) {}
    const form = targetBtn.closest('form');
    if (form && typeof form.requestSubmit === 'function') {
      form.requestSubmit(targetBtn); // proper submitter — sends locale_code=preferred
    } else {
      targetBtn.click(); // fallback for older browsers
    }
  })();

  /* --------------------------------------------------------------------------
     Language switcher dropdown
     -------------------------------------------------------------------------- */
  const langSwitcher = document.querySelector('[data-lang-switcher]');
  if (langSwitcher) {
    const langTrigger = langSwitcher.querySelector('[data-lang-trigger]');
    const closeLang = function() {
      langSwitcher.classList.remove('is-open');
      langTrigger?.setAttribute('aria-expanded', 'false');
    };
    langTrigger?.addEventListener('click', function(e) {
      e.stopPropagation();
      const isOpen = langSwitcher.classList.toggle('is-open');
      langTrigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    });
    document.addEventListener('click', function(e) {
      if (!langSwitcher.classList.contains('is-open')) return;
      if (langSwitcher.contains(e.target)) return;
      closeLang();
    });
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' && langSwitcher.classList.contains('is-open')) {
        closeLang();
        langTrigger?.focus();
      }
    });
  }

  /* --------------------------------------------------------------------------
     Floating contact widget — hides when footer is in view so it doesn't cover
     the social icons / copyright row. (Sticky CTA bar deleted PHASE-32; the
     scroll-threshold show/hide pattern was removed with it.)
     -------------------------------------------------------------------------- */
  const floatingContact = document.querySelector('[data-floating-contact]');
  if (floatingContact) {
    const footer = document.querySelector('.site-footer');
    if (footer && 'IntersectionObserver' in window) {
      const io = new IntersectionObserver(function(entries) {
        const footerVisible = entries[0].isIntersecting;
        floatingContact.classList.toggle('is-hidden-near-footer', footerVisible);
      }, { rootMargin: '0px 0px -120px 0px' });
      io.observe(footer);
    }
  }

  /* --------------------------------------------------------------------------
     Back to top (audit FN-04)
     -------------------------------------------------------------------------- */
  const backToTop = document.querySelector('[data-back-to-top]');
  if (backToTop) {
    const prefersReducedBtt = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const toggleBackToTop = function() {
      backToTop.classList.toggle('is-visible', window.pageYOffset > 600);
    };
    toggleBackToTop();
    window.addEventListener('scroll', toggleBackToTop, { passive: true });
    backToTop.addEventListener('click', function() {
      window.scrollTo({ top: 0, behavior: prefersReducedBtt ? 'auto' : 'smooth' });
    });
  }

  /* --------------------------------------------------------------------------
     Tabs component
     -------------------------------------------------------------------------- */
  document.querySelectorAll('[data-tabs]').forEach(function(container) {
    const tabs = container.querySelectorAll('[data-tab]');
    const panels = container.querySelectorAll('[data-tab-panel]');

    function activate(key) {
      tabs.forEach(function(t) {
        const match = t.dataset.tab === key;
        t.setAttribute('aria-selected', match ? 'true' : 'false');
        t.tabIndex = match ? 0 : -1;
      });
      panels.forEach(function(p) {
        p.classList.toggle('is-active', p.dataset.tabPanel === key);
      });
    }

    tabs.forEach(function(tab) {
      tab.addEventListener('click', function() { activate(this.dataset.tab); });
      tab.addEventListener('keydown', function(e) {
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
        e.preventDefault();
        const list = Array.from(tabs);
        const idx = list.indexOf(this);
        const next = e.key === 'ArrowRight'
          ? list[(idx + 1) % list.length]
          : list[(idx - 1 + list.length) % list.length];
        next.focus();
        activate(next.dataset.tab);
      });
    });

    // Activate first by default
    if (tabs.length) activate(tabs[0].dataset.tab);
  });

  /* --------------------------------------------------------------------------
     Scroll Reveal Animations
     -------------------------------------------------------------------------- */
  const revealElements = document.querySelectorAll('[data-reveal]');
  if (revealElements.length > 0 && 'IntersectionObserver' in window) {
    const revealObserver = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-revealed');
          revealObserver.unobserve(entry.target);
        }
      });
    }, { threshold: 0, rootMargin: '0px 0px 200px 0px' }); /* PHASE-69 / 6.13: fire observer earlier — threshold 0.1→0 + rootMargin -40px→+200px so reveal completes BEFORE element enters viewport, eliminating blank-cream-during-scroll on Safari (browsers without animation-timeline) */

    revealElements.forEach(function(el) {
      revealObserver.observe(el);
    });
  }

  /* --------------------------------------------------------------------------
     Footer Accordion (Mobile)
     -------------------------------------------------------------------------- */
  /* CSS-driven footer accordion (Phase 5): toggle class only.
     §2.3 — triggers are now <button aria-expanded>; keep the attribute honest.
     On desktop the columns are always expanded (no collapse in CSS), so the
     buttons report "true" and clicking is a no-op. On mobile they reflect the
     column's is-open state. */
  const footerMq = window.matchMedia('(max-width: 768px)');
  function syncFooterAria() {
    document.querySelectorAll('[data-footer-accordion]').forEach(function(btn) {
      const column = btn.closest('[data-footer-column]');
      btn.tabIndex = footerMq.matches ? 0 : -1;
      if (!footerMq.matches) {
        btn.setAttribute('aria-expanded', 'true');
      } else {
        btn.setAttribute('aria-expanded', column && column.classList.contains('is-open') ? 'true' : 'false');
      }
    });
  }
  document.querySelectorAll('[data-footer-accordion]').forEach(function(trigger) {
    trigger.addEventListener('click', function() {
      if (!footerMq.matches) return;
      const column = trigger.closest('[data-footer-column]');
      if (!column) return;
      const open = column.classList.toggle('is-open');
      trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
  });
  syncFooterAria();
  if (footerMq.addEventListener) footerMq.addEventListener('change', syncFooterAria);

  /* --------------------------------------------------------------------------
     §5.5 — GA4 conversion event tracking. No-op unless GA4 is configured
     (gtag present). Form *submits* fire generate_lead from each form's
     success-state markup (real conversions only); here we wire download and
     outbound-contact clicks via delegation so dynamically-rendered links work.
     -------------------------------------------------------------------------- */
  function bscTrack(eventName, params) {
    if (typeof window.gtag === 'function') {
      try { window.gtag('event', eventName, params || {}); } catch (e) { /* never break UX for analytics */ }
    }
  }
  window.bscTrack = bscTrack;
  document.addEventListener('click', function(e) {
    const dl = e.target.closest('[data-ga-event]');
    if (dl) {
      const params = {};
      if (dl.dataset.gaName) params.file_name = dl.dataset.gaName;
      const href = dl.getAttribute && dl.getAttribute('href');
      if (href) params.link_url = href;
      bscTrack(dl.dataset.gaEvent, params);
    }
    const wa = e.target.closest('a[href*="wa.me"]');
    if (wa) { bscTrack('whatsapp_click', { link_url: wa.getAttribute('href') }); }
  });

  /* --------------------------------------------------------------------------
     §5.7(a) — Honeypot spam trap. Capture phase so it runs before the form's
     own validation/submit handlers and can cancel a bot submission outright.

     ⚠️ This used to cancel SILENTLY — no message to the buyer, no signal to us.
     A password manager filling the trap field therefore destroyed a real enquiry
     with no trace anywhere, and nobody could even count how often it happened.
     Three things changed on 2026-08-31, and all three matter:
       1. it reports itself to GA4, so a false positive is measurable;
       2. it tells the buyer, so they are not left staring at a dead button;
       3. it CLEARS the trap, so their retry goes through. Autofill fires on load
          and focus, not on a resubmit, so the second attempt succeeds.
     A bot loses nothing by this — bots almost all POST directly and never run
     this handler; hCaptcha is the real defence.
     -------------------------------------------------------------------------- */
  function showHoneypotNotice(form, message) {
    if (!message) return;
    var box = form.querySelector('[data-hp-notice]');
    if (!box) {
      box = document.createElement('div');
      box.className = 'form-message--error';
      box.setAttribute('data-hp-notice', '');
      box.setAttribute('role', 'alert');
      box.setAttribute('tabindex', '-1');
      form.insertBefore(box, form.firstChild);
    }
    box.textContent = message;
    try { box.focus(); } catch (e) { /* focus is a nicety, never break submit UX */ }
  }

  document.addEventListener('submit', function(e) {
    const form = e.target;
    const hp = form && form.querySelector && form.querySelector('[data-honeypot]');
    if (!hp || hp.value.trim() === '') return;
    e.preventDefault();
    e.stopImmediatePropagation();
    bscTrack('form_blocked_honeypot', {
      form_action: (form.getAttribute && form.getAttribute('action')) || ''
    });
    hp.value = '';
    showHoneypotNotice(form, hp.getAttribute('data-hp-message'));
  }, true);

  /* --------------------------------------------------------------------------
     Animated stat counters — Wave I
     --------------------------------------------------------------------------
     Numbers in `.stat__number`, `.prg-stat__number`, `.team-stat__number`,
     and any element with [data-counter] tick up from 0 to their final value
     when they enter the viewport. Parses leading number from text content
     so suffixes like "+", " kg/day", "%", etc. are preserved. */
  function animateCounter(el) {
    const original = el.textContent.trim();
    // Pull out the leading numeric portion (handles 1971, 1,250, 14.5, ± 28)
    const match = original.match(/^([±+\-]?\s?[\d.,]+)([\s\S]*)$/);
    if (!match) return;
    const raw = match[1].replace(/[±+\-,\s]/g, '');
    const target = parseFloat(raw);
    const suffix = match[2];
    const prefix = match[1].match(/^[±+\-]?\s?/)[0];
    if (isNaN(target) || target === 0) return;

    // POST-CUSTOMER-AUDIT 2026-05-21 — Skip animation for categorical stats
    // like "16GG", "3GG", "63+", "48hr" where the value is a label, not a
    // count. The 0→target ramp showed nonsense midpoints ("3GG FINEST GAUGE"
    // when the label is supposed to read "16GG is the finest"). Heuristic:
    // suffix begins with a letter/+/% with no leading space, OR target ≤ 9.
    if (target <= 9 || /^[A-Za-z+%]/.test(suffix)) return;

    // §2.6 — reduced-motion: skip the 0→target ramp, leave the final value shown
    // (el.textContent is still `original` at this point). WCAG 2.3.3.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const duration = Math.min(1000, 500 + Math.log10(Math.max(target, 10)) * 200);
    const start = performance.now();
    const isFloat = match[1].includes('.');
    const decimals = isFloat ? (match[1].split('.')[1] || '').length : 0;

    function step(now) {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3); // ease-out cubic
      const current = target * eased;
      const formatted = isFloat ? current.toFixed(decimals) : Math.round(current).toLocaleString();
      el.textContent = prefix + formatted + suffix;
      if (t < 1) requestAnimationFrame(step);
      else el.textContent = original;
    }

    el.textContent = prefix + (isFloat ? '0' + (decimals ? '.' + '0'.repeat(decimals) : '') : '0') + suffix;
    requestAnimationFrame(step);
  }

  if ('IntersectionObserver' in window) {
    const counterIO = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (entry.isIntersecting && !entry.target.dataset.counted) {
          entry.target.dataset.counted = '1';
          animateCounter(entry.target);
          counterIO.unobserve(entry.target);
        }
      });
    }, { threshold: 0.4 });

    document.querySelectorAll('.stat__number, .prg-stat__number, .team-stat__number, [data-counter]').forEach(function(el) {
      counterIO.observe(el);
    });
  }

  /* --------------------------------------------------------------------------
     PDP content-derived tint — Phase 11 (Wave H)
     --------------------------------------------------------------------------
     Sample the dominant non-neutral hue from the product hero image and
     publish it as `--pdp-tint` on <html>. CSS color-mix() then uses that
     value to subtly tint chrome on the product page only. */
  (function pdpTint() {
    if (!document.body.classList.contains('template-page-product-detail') &&
        !document.body.classList.contains('template-page-product')) return;
    const hero = document.querySelector('.pd-gallery img, .pd-hero img, .product__image img, [data-product-hero] img');
    if (!hero) return;
    const apply = function() {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = 8; canvas.height = 8;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        ctx.drawImage(hero, 0, 0, 8, 8);
        const data = ctx.getImageData(0, 0, 8, 8).data;
        let r = 0, g = 0, b = 0, n = 0;
        for (let i = 0; i < data.length; i += 4) {
          const cr = data[i], cg = data[i + 1], cb = data[i + 2];
          // Skip near-neutral pixels (greyscale-ish) so the tint is hue-driven
          if (Math.max(cr, cg, cb) - Math.min(cr, cg, cb) > 24) {
            r += cr; g += cg; b += cb; n++;
          }
        }
        if (n === 0) return;
        r = Math.round(r / n); g = Math.round(g / n); b = Math.round(b / n);
        document.documentElement.style.setProperty('--pdp-tint', `rgb(${r} ${g} ${b})`);
      } catch (e) { /* CORS or other; safe to ignore */ }
    };
    if (hero.complete && hero.naturalHeight !== 0) apply();
    else hero.addEventListener('load', apply, { once: true });
  })();

  /* --------------------------------------------------------------------------
     Quality polish — Phase 12 (Wave H)
     --------------------------------------------------------------------------
     Keyboard shortcuts and global Esc-to-close behavior. */
  document.addEventListener('keydown', function(e) {
    // "/" focuses search (skip when in input/textarea)
    const tag = e.target.tagName;
    const inField = tag === 'INPUT' || tag === 'TEXTAREA' || e.target.isContentEditable;

    if (e.key === '/' && !inField && !e.metaKey && !e.ctrlKey) {
      const searchTrigger = document.querySelector('[data-search-trigger]');
      const searchDrawerEl = document.querySelector('.search-drawer');
      // Open-only — never toggle closed (§1.4).
      if (searchTrigger && !searchDrawerEl?.classList.contains('is-open')) {
        e.preventDefault();
        searchTrigger.click();
        setTimeout(function() {
          document.querySelector('.search-drawer__input')?.focus();
        }, 60);
      }
    }
    // Cmd+K / Ctrl+K is handled by the search section's own keydown listener (§1.4 — de-duplicated to stop the flash-closed race).
    // Escape — close any open drawer/menu
    if (e.key === 'Escape') {
      // Close drawers.
      // NOTE: the sample-cart drawer is intentionally NOT closed here — it owns a
      // dedicated Escape handler (below) that also removes `inert`. If this generic
      // handler stripped `is-open` first, that handler would early-return and inert
      // would never be cleared, leaving the whole page dead. (§1.1)
      document.querySelectorAll('.search-drawer.is-open, .mobile-drawer.is-open, [data-cart-drawer].is-open, [data-wishlist-drawer].is-open').forEach(function(el) {
        el.classList.remove('is-open');
      });
      document.querySelectorAll('.mobile-overlay.is-active, [data-cart-overlay].is-active, [data-wishlist-overlay].is-active').forEach(function(el) {
        el.classList.remove('is-active');
      });
      // Close any open popovers — only where the Popover API exists. On older browsers
      // `el.matches(':popover-open')` throws a SyntaxError and aborts the rest of this
      // handler (so Escape would stop closing drawers). §1.6
      if ('hidePopover' in HTMLElement.prototype) {
        document.querySelectorAll('[popover]').forEach(function(el) {
          if (el.matches(':popover-open')) el.hidePopover();
        });
      }
      document.body.style.overflow = '';
    }
  });

  /* --------------------------------------------------------------------------
     Progressive image reveal — Phase 6
     --------------------------------------------------------------------------
     Mark each lazy-loaded image with [data-loaded] when its `load` event
     fires. CSS handles the fade-in. Also handles the .prog-image wrapper
     pattern (LQIP placeholder + full crossfade). */
  function markLoaded(img) {
    img.setAttribute('data-loaded', '');
    const wrapper = img.closest('[data-prog]');
    if (wrapper) wrapper.setAttribute('data-loaded', '');
  }
  document.querySelectorAll('img[loading="lazy"], img[data-prog-full]').forEach(function(img) {
    if (img.complete && img.naturalHeight !== 0) {
      markLoaded(img);
    } else {
      img.addEventListener('load', function() { markLoaded(img); }, { once: true });
      img.addEventListener('error', function() { markLoaded(img); }, { once: true });
    }
  });

  /* --------------------------------------------------------------------------
     SAMPLE CART — persistent localStorage-backed multi-item sample request
     -------------------------------------------------------------------------- */
  const SAMPLE_CART_KEY = 'bsc-sample-cart-v1';
  const SAMPLE_CART_EVENT = 'bsc:sample-cart:change';

  const SampleCart = {
    /* ---- state ---- */
    _mem: null, // §1.9(e) — in-memory fallback used only when localStorage is unavailable
    _read() {
      if (this._mem) return this._mem.slice();
      try {
        const raw = localStorage.getItem(SAMPLE_CART_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        return this._mem ? this._mem.slice() : [];
      }
    },

    _write(items) {
      try {
        localStorage.setItem(SAMPLE_CART_KEY, JSON.stringify(items));
        this._mem = null; // storage works — read fresh each time (respects cross-tab changes)
      } catch (e) {
        // §1.9(e) — storage blocked (private mode / quota). Keep an in-memory copy so the cart
        // still works this session and add() doesn't announce "added" over an empty cart.
        this._mem = items.slice();
      }
      window.dispatchEvent(new CustomEvent(SAMPLE_CART_EVENT, { detail: { items } }));
    },

    get() { return this._read(); },

    count() { return this._read().length; },

    has(id) { return this._read().some(i => i.id === id); },

    /* ---- actions ---- */
    add(item) {
      if (!item || !item.id) return false;
      const items = this._read();
      if (items.some(i => i.id === item.id)) return false;
      items.push({
        id: item.id,
        type: item.type || 'item',
        title: item.title || '',
        reference: item.reference || '',
        context: item.context || '',
        colour: item.colour || '',
        image: item.image || '',
        availability: item.availability || '',
        price_label: item.price_label || '',
        added_at: Date.now()
      });
      this._write(items);
      return true;
    },

    remove(id) {
      const items = this._read().filter(i => i.id !== id);
      this._write(items);
    },

    /* The colour a garment was added in (visitor forum b2b-007, 28 Sep 2026). Product pages
       pass their "Shown in …" line, which every locale words differently but which always
       carries the colour card's English name and code: "Mostrato in Camel (8100) · …",
       "掲載色：Camel（8100）", "Camel(8100)로 제작한 모습". Take "Name (code)" from it; else the
       colour after " · " in the title ("Crewneck Sweater · Organic Brown"); else the line's
       first part plus its archive code ("Navy — archive colour ref 2950" → "Navy (2950)").
       Spanish lines open with a capitalised "En" ("En Red (5500) · disponible…"), which the
       name pattern took as part of the colour ("En Red (5500)"); it is dropped. No colour name
       on the site starts with "En" (checked 7 Oct 2026). */
    colourFrom(src, title) {
      const line = String(src || '').replace(/\uFF08/g, '(').replace(/\uFF09/g, ')');
      const coded = line.match(/([A-Z][A-Za-z]*(?:[ \/-][A-Z][A-Za-z]*)*)\s?\((\d{2,5})\)/);
      if (coded) return coded[1].replace(/^En\s+/, '').trim() + ' (' + coded[2] + ')';
      const t = String(title || '');
      const dot = t.lastIndexOf(' · ');
      if (dot > -1) return t.slice(dot + 3).trim();
      const first = line.split(/\s[—–·]\s|\.\s/)[0].trim();
      if (!first || first.length > 60) return '';
      const ref = line.match(/(?:^|[^\w])(\d{3,4})(?![\w])/);   // "archive colour ref 500" in any locale
      return ref ? first + ' (' + ref[1] + ')' : first;
    },

    clear() { this._write([]); },

    /* ---- UI render ---- */
    render() {
      this._renderBadge();
      this._renderDrawer();
      this._syncButtons();
    },

    _renderBadge() {
      const n = this.count();
      document.querySelectorAll('[data-sample-cart-count]').forEach(el => {
        el.textContent = n;
        el.closest('[data-sample-cart-trigger]')?.setAttribute('data-count', n);
      });
      const trigger = document.querySelector('[data-sample-cart-trigger]');
      if (trigger) trigger.classList.toggle('is-populated', n > 0);
    },

    _renderDrawer() {
      const list = document.querySelector('[data-sample-cart-list]');
      const empty = document.querySelector('[data-sample-cart-empty]');
      const populated = document.querySelector('[data-sample-cart-populated]');
      if (!list) return;

      const items = this._read();

      // LOCK-SC1 — Toggle clear-all wrapper (header) visibility based on populated state.
      // Hide the wrap entirely when cart is empty + reset any in-flight confirm state.
      // Done up-front so the empty-state early return below doesn't skip it.
      const clearWrap = document.querySelector('[data-sample-cart-clear-wrap]');
      if (clearWrap) {
        clearWrap.hidden = items.length === 0;
        if (items.length === 0) clearWrap.classList.remove('is-confirming');
      }

      if (items.length === 0) {
        list.innerHTML = '';
        if (empty) empty.hidden = false;
        if (populated) populated.hidden = true;
        return;
      }
      if (empty) empty.hidden = true;
      if (populated) populated.hidden = false;

      list.innerHTML = items.map(item => {
        // PHASE-27 D2/D3 — dot + neutral text pattern; data-avail drives the dot color
        const availBadge = item.availability
          ? `<span class="sc-item__avail" data-avail="${this._esc(item.availability)}">
               <span class="sc-item__avail-dot" aria-hidden="true"></span>
               <span class="sc-item__avail-label label-secondary">${this._availLabel(item.availability)}</span>
             </span>`
          : '';
        const ctx = item.context ? `<span class="sc-item__ctx">${this._esc(item.context)}</span>` : '';
        // Skip the colour when the title already ends with it ("… · Charcoal").
        const colour = item.colour && !String(item.title || '').endsWith(' · ' + item.colour)
          ? `<span class="sc-item__colour">${this._esc(item.colour)}</span>` : '';
        const ref = item.reference ? `<span class="sc-item__ref label-secondary">${this._esc(item.reference)}</span>` : '';
        const price = item.price_label ? `<span class="sc-item__price">${this._esc(item.price_label)}</span>` : '';
        const img = item.image
          ? `<img src="${this._esc(item.image)}" alt="${this._esc(item.title)}" loading="lazy">`
          : `<div class="sc-item__ph"></div>`;
        return `
          <li class="sc-item" data-id="${this._esc(item.id)}">
            <div class="sc-item__image">${img}</div>
            <div class="sc-item__body">
              ${ref}
              <span class="sc-item__title">${this._esc(item.title)}</span>
              ${colour}
              ${ctx}
              <div class="sc-item__meta">${availBadge}${price}</div>
            </div>
            <button type="button" class="sc-item__remove" data-sample-cart-remove="${this._esc(item.id)}" aria-label="Remove ${this._esc(item.title)}">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="square" stroke-linejoin="miter" aria-hidden="true"><path d="M18 6L6 18M6 6l12 12"/></svg>
            </button>
          </li>
        `;
      }).join('');

      /* LOCK-SC1 clear-wrap toggle moved to the top of _renderDrawer (before the empty-state early return). */
    },

    _syncButtons() {
      const ids = new Set(this._read().map(i => i.id));
      document.querySelectorAll('[data-sample-add]').forEach(btn => {
        const isAdded = ids.has(btn.getAttribute('data-id'));
        if (!btn.getAttribute('data-id')) return;
        btn.classList.toggle('is-added', isAdded);
        btn.setAttribute('aria-pressed', isAdded ? 'true' : 'false');
        // PHASE-28 D7 — native title attr replaces width-flexing ::after suffix
        if (isAdded) {
          btn.setAttribute('title', 'Tap to remove');
        } else {
          btn.removeAttribute('title');
        }
      });
    },

    /* D24 — aria-live announcement helper. Clear-then-set with 50ms delay
       ensures screen readers re-announce duplicate strings. */
    announce(msg) {
      const el = document.querySelector('[data-sample-cart-announce]');
      if (!el) return;
      el.textContent = '';
      setTimeout(() => { el.textContent = msg; }, 50);
    },

    _availLabel(v) {
      switch (v) {
        case 'in-stock':     return 'In stock';
        case 'limited':      return 'Limited';
        case 'made-to-order':return 'Made to order';
        case 'discontinued': return 'Discontinued';
        default: return v;
      }
    },

    _esc(s) {
      return String(s == null ? '' : s)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    }
  };

  // Expose for other scripts (samples form)
  window.BSCSampleCart = SampleCart;

  /* --------------------------------------------------------------------------
     SAMPLE CART — DOM wiring
     -------------------------------------------------------------------------- */

  // Drawer open/close (PHASE-27 D14 — focus trap + ESC + restore-focus + D25 inert)
  const scDrawer = document.querySelector('[data-sample-cart-drawer]');
  const scOverlay = document.querySelector('[data-sample-cart-overlay]');
  let scLastFocused = null;

  function openSampleCart() {
    if (!scDrawer) return;
    scLastFocused = document.activeElement;
    scDrawer.classList.add('is-open');
    // Force the scrim hit-testable inline — beats the anti-FOUC `pointer-events:none`
    // rule regardless of CSS cascade/caching, so a click on the dimmed area (which
    // otherwise falls through to the inert page behind) reliably reaches the overlay.
    if (scOverlay) { scOverlay.classList.add('is-active'); scOverlay.style.pointerEvents = 'auto'; }
    document.body.style.overflow = 'hidden';
    // D25 — set inert on background siblings so they're not tabbable / readable by AT.
    // CRITICAL: skip elements that are INSIDE the drawer itself (the drawer markup
    // uses semantic <header> + <footer> tags that match the selector), and skip
    // ancestors that contain the drawer. Otherwise the drawer's own footer
    // becomes inert and the Review & Submit link can't be clicked.
    document.querySelectorAll('main, header, footer').forEach(el => {
      if (scDrawer.contains(el)) return;       // skip drawer's own <header>/<footer>
      if (el.contains(scDrawer)) return;       // skip any ancestor of drawer (defensive)
      el.setAttribute('inert', '');
    });
    // Focus first VISIBLE interactive element. A hidden wrapper (e.g. the empty-cart
    // "Clear all" button) matches :not([hidden]) but has offsetParent === null, so focus()
    // silently fails and the dialog opens with no focus. Filter by visibility. §1.7
    const scFocusables = scDrawer.querySelectorAll('button:not([hidden]), a[href], input:not([disabled]), [tabindex]:not([tabindex="-1"])');
    const firstFocusable = Array.prototype.find.call(scFocusables, function(el) { return el.offsetParent !== null; });
    if (firstFocusable) {
      // Focus SYNCHRONOUSLY first, then retry on the next frame only if it did not
      // take. Reading offsetParent above already forced layout, so the drawer is
      // rendered by this point and the synchronous call normally succeeds.
      // Why not rAF alone: rAF is throttled to zero in a backgrounded or hidden
      // tab, so the dialog could open with focus left on <body> — which is exactly
      // what an automated accessibility check sees, and what a real user gets if the
      // drawer is opened from a tab that is not frontmost. Belt and braces; the
      // second call is a no-op when the first worked.
      firstFocusable.focus();
      if (document.activeElement !== firstFocusable) {
        requestAnimationFrame(() => firstFocusable.focus());
      }
    }
  }
  function closeSampleCart() {
    if (!scDrawer) return;
    scDrawer.classList.remove('is-open');
    if (scOverlay) { scOverlay.classList.remove('is-active'); scOverlay.style.pointerEvents = ''; }
    document.body.style.overflow = '';
    // Remove inert from background
    document.querySelectorAll('[inert]').forEach(el => el.removeAttribute('inert'));
    // Restore focus
    if (scLastFocused && typeof scLastFocused.focus === 'function') {
      try { scLastFocused.focus(); } catch (_) { /* element may have been removed */ }
    }
    scLastFocused = null;
  }

  // Click handlers (event delegation so buttons added dynamically still work)
  document.addEventListener('click', function(e) {
    const trigger = e.target.closest('[data-sample-cart-trigger]');
    if (trigger) { e.preventDefault(); openSampleCart(); return; }

    const closeBtn = e.target.closest('[data-sample-cart-close]');
    if (closeBtn) { e.preventDefault(); closeSampleCart(); return; }

    const overlay = e.target.closest('[data-sample-cart-overlay]');
    if (overlay) { closeSampleCart(); return; }

    const addBtn = e.target.closest('[data-sample-add]');
    if (addBtn) {
      e.preventDefault();
      // PHASE-27 D7 — ripple killed; no class-toggle, no scale transform
      const id = addBtn.getAttribute('data-id');
      if (!id) return;
      const title = addBtn.getAttribute('data-title') || 'Item';
      if (SampleCart.has(id)) {
        SampleCart.remove(id);
        SampleCart.announce(title + ' removed.');
      } else {
        SampleCart.add({
          id: id,
          type:         addBtn.getAttribute('data-type') || 'item',
          title:        addBtn.getAttribute('data-title') || '',
          reference:    addBtn.getAttribute('data-reference') || '',
          context:      addBtn.getAttribute('data-context') || '',
          colour:       addBtn.getAttribute('data-colour') ||
                        (addBtn.hasAttribute('data-colour-src')
                          ? SampleCart.colourFrom(addBtn.getAttribute('data-colour-src'), addBtn.getAttribute('data-title'))
                          : ''),
          image:        addBtn.getAttribute('data-image') || '',
          availability: addBtn.getAttribute('data-availability') || '',
          price_label:  addBtn.getAttribute('data-price') || ''
        });
        SampleCart.announce(title + ' added to sample cart.');
        /* F4 (2026-05-28): do NOT auto-open the drawer on every add. The drawer
           slides in from the right and overlays adjacent swatch buttons on
           dense grids (Colour Card especially), making it impossible to add a
           second item without first dismissing the drawer (Cowork smoke test).
           The floating sample-cart pill at the bottom-right updates its count
           on every add via the `bsc:sample-cart:change` event, giving the user
           visual feedback. Users open the full drawer when ready by clicking
           the pill. Matches conventional e-commerce UX (Amazon, etc). */
      }
      return;
    }

    const removeBtn = e.target.closest('[data-sample-cart-remove]');
    if (removeBtn) {
      e.preventDefault();
      const id = removeBtn.getAttribute('data-sample-cart-remove');
      SampleCart.remove(id);
      return;
    }

    // BUGFIX 2026-05-16: replaced two-step confirm with single-click clear.
    // The previous two-step flow (Clear all → Yes/Cancel pair) appeared visually
    // identical to the user — the confirm pair is so subtle in the same header row
    // that users perceived the first click as "nothing happened" or "drawer closed",
    // then 5s auto-revert masked the bug. Sample cart is local-state only and items
    // can be re-added in seconds, so confirmation overhead is unnecessary friction.
    const clearBtn = e.target.closest('[data-sample-cart-clear]');
    if (clearBtn) {
      e.preventDefault();
      SampleCart.clear();
      SampleCart.announce('Sample cart cleared.');
      return;
    }

    // Click anywhere outside the open drawer closes it (backdrop scrim OR the page
    // behind it). Runs LAST, so the trigger/close/add/remove/clear handlers above
    // take precedence — clicking an add-to-samples button still adds without closing.
    // Robust even if the scrim isn't hit-testable: we test the target, not the overlay.
    if (scDrawer && scDrawer.classList.contains('is-open') &&
        !e.target.closest('[data-sample-cart-drawer]') &&
        !e.target.closest('[data-sample-cart-trigger]')) {
      closeSampleCart();
    }
  });

  // ESC closes drawer; Tab traps focus within drawer when open (D14)
  document.addEventListener('keydown', function(e) {
    if (!scDrawer || !scDrawer.classList.contains('is-open')) return;
    if (e.key === 'Escape') {
      closeSampleCart();
      return;
    }
    if (e.key === 'Tab') {
      const focusables = Array.prototype.filter.call(scDrawer.querySelectorAll(
        'button:not([hidden]):not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      ), function(el) { return el.offsetParent !== null; }); // visible only — §1.7
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  });

  // Listen to state changes → re-render
  window.addEventListener(SAMPLE_CART_EVENT, function() { SampleCart.render(); });

  // Cross-tab sync — if user has cart open in two tabs, reflect changes
  window.addEventListener('storage', function(e) {
    // §1.5 — change from ANOTHER tab. Re-render the drawer AND re-fire the change event so any
    // listening page (the samples cart-review + its hidden form fields) re-serializes too.
    if (e.key === SAMPLE_CART_KEY) {
      SampleCart.render();
      window.dispatchEvent(new CustomEvent(SAMPLE_CART_EVENT, { detail: { items: SampleCart.get() } }));
    }
  });

  // Initial render
  document.addEventListener('DOMContentLoaded', function() { SampleCart.render(); });
  // In case DOMContentLoaded already fired
  if (document.readyState !== 'loading') SampleCart.render();

  /* PHASE-PRE-LAUNCH: post-submission cart wipe.
     When the samples form posts successfully, the rendered page contains a
     [data-samples-submitted] marker (see b2b-samples.liquid thank-you state).
     Detect it and clear the persisted cart so the floating pill and review
     panel don't keep showing items the user already requested — prevents
     accidental duplicate-fulfillment submissions. */
  function clearSampleCartOnSuccess() {
    if (document.querySelector('[data-samples-submitted]')) {
      SampleCart.clear();
    }
  }
  document.addEventListener('DOMContentLoaded', clearSampleCartOnSuccess);
  if (document.readyState !== 'loading') clearSampleCartOnSuccess();

  /* Bulk add — "Add entire palette to samples" on seasonal pages */
  document.addEventListener('click', function(e) {
    const btn = e.target.closest('[data-ssn-add-all]');
    if (!btn) return;
    e.preventDefault();
    const raw = btn.getAttribute('data-palette-items');
    if (!raw) return;
    let items;
    try { items = JSON.parse(raw); } catch (err) { console.error('Invalid palette JSON:', err); return; }
    if (!Array.isArray(items) || items.length === 0) return;

    let added = 0;
    items.forEach(function(item) {
      if (!SampleCart.has(item.id)) {
        if (SampleCart.add(item)) added++;
      }
    });

    // Open drawer so the user sees the result — route through openSampleCart() so inert +
    // focus management run. A raw classList.add left the background tabbable/AT-readable. §1.9(d)
    if (scDrawer) openSampleCart();

    // Brief feedback on the button. Buttons that carry data-added-label / data-already-label
    // (the winter colour stories, 26 Sep 2026) get it in the visitor's language and keep their
    // icon: only the [data-bulk-label] part changes. Others keep the original English text.
    const label = btn.querySelector('[data-bulk-label]') || btn;
    const originalHTML = label.innerHTML;
    const addedText = btn.getAttribute('data-added-label');
    const alreadyText = btn.getAttribute('data-already-label');
    label.textContent = added > 0 ? (addedText || (added + ' added to samples ✓')) : (alreadyText || 'Already in samples');
    btn.setAttribute('disabled', 'disabled');
    setTimeout(function() {
      label.innerHTML = originalHTML;
      btn.removeAttribute('disabled');
    }, 2400);
  });

  /* --------------------------------------------------------------------------
     PDF downloads — fetch-to-blob so the file SAVES rather than opening a
     cdn.shopify.com tab.

     The markup keeps a real <a href> to the CDN, so this is pure enhancement:
     no JS, or any failure below, falls through to the browser's own behaviour.

     Why it's needed: the `download` attribute is ignored on cross-origin URLs,
     and Shopify's CDN sends no Content-Disposition, so the PDF would open in a
     new tab showing the raw CDN URL. Shopify's CDN does send
     `access-control-allow-origin: *`, which makes the fetch below legal.
     -------------------------------------------------------------------------- */
  document.addEventListener('click', function(e) {
    const link = e.target.closest('a[data-pdf-download]');
    if (!link) return;

    const href = link.getAttribute('href');
    if (!href) return;
    // Let modified clicks (new tab, save-as) behave normally.
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    // Nothing to gain on a same-origin file — the attribute already works there.
    try {
      if (new URL(href, window.location.href).origin === window.location.origin) return;
    } catch (err) { return; }

    e.preventDefault();

    const filename = link.dataset.pdfDownload || 'download.pdf';
    const label = link.querySelector('.spec-pdf-btn__label') || link;
    const original = label.textContent;
    let settled = false;

    function restore() {
      if (settled) return;
      settled = true;
      label.textContent = original;
      link.removeAttribute('aria-busy');
    }
    // If anything hangs, put the button back rather than leaving it stuck.
    const guard = setTimeout(restore, 20000);

    label.textContent = (link.dataset.pdfDownloadingLabel || 'Preparing…');
    link.setAttribute('aria-busy', 'true');

    fetch(href, { mode: 'cors', credentials: 'omit' })
      .then(function(res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.blob();
      })
      .then(function(blob) {
        const objUrl = URL.createObjectURL(blob);
        const tmp = document.createElement('a');
        tmp.href = objUrl;
        tmp.download = filename;
        document.body.appendChild(tmp);
        tmp.click();
        document.body.removeChild(tmp);
        // Revoke late — Safari reads the blob after the click returns.
        setTimeout(function() { URL.revokeObjectURL(objUrl); }, 10000);
        clearTimeout(guard);
        restore();
      })
      .catch(function() {
        // CORS pulled, offline, or the file moved — hand it back to the browser.
        clearTimeout(guard);
        restore();
        window.open(href, '_blank', 'noopener');
      });
  });


  /* --------------------------------------------------------------------------
     Filter strips follow the keyboard (a11y audit 7 Oct 2026, BSC-06)
     --------------------------------------------------------------------------
     The colour card and other pages draw radio / checkbox filters as labels in a
     strip that scrolls sideways on phones. Arrow keys move the choice, but the strip
     did not move with it, so the chosen label could sit off screen. When a hidden
     input gets focus or changes, scroll its label's strip just enough to show it. */
  function bscFollowChoice(e) {
    const input = e.target;
    if (!input || input.tagName !== 'INPUT' || (input.type !== 'radio' && input.type !== 'checkbox') || !input.id) return;
    let label = null;
    try { label = document.querySelector('label[for="' + CSS.escape(input.id) + '"]'); } catch (_) { return; }
    if (!label) return;
    for (let p = label.parentElement; p && p !== document.body; p = p.parentElement) {
      if (p.scrollWidth <= p.clientWidth + 1) continue;
      const ox = getComputedStyle(p).overflowX;
      if (ox !== 'auto' && ox !== 'scroll') continue;
      const lr = label.getBoundingClientRect();
      const pr = p.getBoundingClientRect();
      if (lr.left < pr.left) p.scrollLeft -= (pr.left - lr.left) + 16;
      else if (lr.right > pr.right) p.scrollLeft += (lr.right - pr.right) + 16;
      return;
    }
  }
  document.addEventListener('focusin', bscFollowChoice);
  document.addEventListener('change', bscFollowChoice);

})();
